import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@babackapp/customer-ui/components/alert";
import {
  AnimatedButtonContainer,
  AnimatedButtonRow,
  Button,
} from "@babackapp/customer-ui/components/button";
import {
  Header,
  HeaderDescription,
  HeaderTitle,
} from "@babackapp/customer-ui/components/header";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from "@babackapp/customer-ui/components/input-group";
import { Separator } from "@babackapp/customer-ui/components/separator";
import { WidgetPage } from "@babackapp/customer-ui/components/widget-page";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircleIcon, ArrowLeft, LoaderIcon } from "lucide-react";
import { motion } from "motion/react";
import { Controller, useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { useCustomText } from "../hooks/use-custom-text";
import { usePublicCreateGiftReturn } from "@babackapp/private-api-client/client/use-public-create-gift-return";
import { CountryCombobox } from "../components/country-combobox";
import { deriveCountryName } from "../utils/countries";
import {
  giftAddressSchema,
  type GiftAddressFormValues,
} from "../forms/gift-address.form";

type LocationState = {
  token?: string;
  giftCode?: string;
  last_name: string;
  first_name: string;
  email: string;
  phone: string;
};

export function GiftAddressPage() {
  const navigate = useNavigate();
  const { state } = useLocation() as { state: LocationState };
  const mutation = usePublicCreateGiftReturn();
  const t = useCustomText();

  const { control, handleSubmit } = useForm<GiftAddressFormValues>({
    resolver: zodResolver(giftAddressSchema),
    defaultValues: {
      last_name: state?.last_name ?? "",
      first_name: state?.first_name ?? "",
      address2: "",
      country_code: "FR",
    },
  });

  const onSubmit = async (values: GiftAddressFormValues) => {
    await mutation.mutateAsync({
      token: state?.token,
      giftCode: state?.giftCode,
      email: state?.email,
      phone: state?.phone,
      address: {
        ...values,
        country: deriveCountryName(values.country_code),
      },
    });
    navigate("../complete");
  };

  const errorCode = (mutation.error as { code?: string } | null)?.code;

  return (
    <WidgetPage>
      <Header>
        <HeaderTitle>{t("gift.address.title")}</HeaderTitle>
        <HeaderDescription>{t("gift.address.description")}</HeaderDescription>
      </Header>

      {mutation.isError && (
        <Alert variant="destructive">
          <AlertCircleIcon />
          <AlertTitle>
            {errorCode === "GIFT_CODE_NOT_FOUND"
              ? t("gift.error.notFound")
              : errorCode === "GIFT_CODE_USED"
                ? t("gift.error.alreadyUsed")
                : t("common.error.retry")}
          </AlertTitle>
          <AlertDescription>{t("gift.error.checkAndRetry")}</AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <motion.div
          initial={{ opacity: 0, x: 15 }}
          animate={{ opacity: 1, x: 0, transition: { delay: 0.1 } }}
          exit={{ opacity: 0, x: -15 }}
          transition={{ duration: 0.3 }}
          className="grid grid-cols-6 gap-2"
        >
          <Controller
            control={control}
            name="first_name"
            render={({ field }) => (
              <InputGroup className="h-auto col-start-1 col-span-3">
                <InputGroupInput
                  placeholder={t("common.fields.firstName")}
                  {...field}
                />
                <InputGroupAddon align="block-start">
                  <InputGroupText>
                    {t("common.fields.firstName")}
                  </InputGroupText>
                </InputGroupAddon>
              </InputGroup>
            )}
          />
          <Controller
            control={control}
            name="last_name"
            render={({ field }) => (
              <InputGroup className="h-auto col-start-4 col-span-3">
                <InputGroupInput
                  placeholder={t("common.fields.lastNameShort")}
                  {...field}
                />
                <InputGroupAddon align="block-start">
                  <InputGroupText>{t("common.fields.lastName")}</InputGroupText>
                </InputGroupAddon>
              </InputGroup>
            )}
          />

          <Separator className="col-span-6 my-4" />

          <Controller
            control={control}
            name="address1"
            render={({ field }) => (
              <InputGroup className="h-auto col-start-1 col-span-3">
                <InputGroupInput
                  placeholder={t("common.fields.address1")}
                  {...field}
                />
                <InputGroupAddon align="block-start">
                  <InputGroupText>{t("common.fields.address1")}</InputGroupText>
                </InputGroupAddon>
              </InputGroup>
            )}
          />
          <Controller
            control={control}
            name="address2"
            render={({ field }) => (
              <InputGroup className="h-auto col-start-4 col-span-3">
                <InputGroupInput
                  placeholder={t("common.fields.address2Placeholder")}
                  {...field}
                />
                <InputGroupAddon align="block-start">
                  <InputGroupText>{t("common.fields.address2")}</InputGroupText>
                </InputGroupAddon>
              </InputGroup>
            )}
          />
          <Controller
            control={control}
            name="city"
            render={({ field }) => (
              <InputGroup className="h-auto col-start-1 col-span-2">
                <InputGroupInput
                  placeholder={t("common.fields.city")}
                  {...field}
                />
                <InputGroupAddon align="block-start">
                  <InputGroupText>{t("common.fields.city")}</InputGroupText>
                </InputGroupAddon>
              </InputGroup>
            )}
          />
          <Controller
            control={control}
            name="zip"
            render={({ field }) => (
              <InputGroup className="h-auto col-start-3 col-span-2">
                <InputGroupInput
                  placeholder={t("common.fields.zip")}
                  {...field}
                />
                <InputGroupAddon align="block-start">
                  <InputGroupText>{t("common.fields.zip")}</InputGroupText>
                </InputGroupAddon>
              </InputGroup>
            )}
          />
          <Controller
            control={control}
            name="country_code"
            render={({ field }) => (
              <div className="col-start-5 col-span-2 flex flex-col gap-1">
                <span className="text-xs text-muted-foreground px-0.5">
                  {t("common.fields.country")}
                </span>
                <CountryCombobox
                  value={field.value}
                  onChange={field.onChange}
                  placeholder={t("common.fields.country")}
                />
              </div>
            )}
          />
        </motion.div>

        <AnimatedButtonRow variants={{}}>
          <AnimatedButtonContainer
            layoutId="line-item-back-button"
            disableVariants={{ exit: true }}
          >
            <Button
              variant="secondary"
              type="button"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft />
              {t("common.back")}
            </Button>
          </AnimatedButtonContainer>
          <AnimatedButtonContainer
            layoutId="line-item-confirm-button"
            variants={{}}
          >
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? (
                <LoaderIcon className="animate-spin" />
              ) : (
                t("gift.address.cta")
              )}
            </Button>
          </AnimatedButtonContainer>
        </AnimatedButtonRow>
      </form>
    </WidgetPage>
  );
}
